// Typed client for the advisor agent API (/api/agent, M15). Lives beside the
// feature so web/src/api.ts stays untouched. Same transport rules as the shared
// helper: cookies + the X-Requested-With CSRF header. Turns stream back as SSE
// over a POST, so they are read with fetch + a small SSE parser (EventSource
// cannot POST).

export interface AgentThread {
  id: number;
  user_id: number;
  investor_id: number | null;
  advisory_session_id: number | null;
  qp_flow_instance_id: string | null;
  title: string | null;
  provider: string;
  model: string;
  allow_drafts: number;
  tokens_in: number;
  tokens_out: number;
  cost: number;
  created_at: string;
  updated_at: string;
  investor_name?: string | null;
}

export type EventStatus = "pending_approval" | "approved" | "running" | "ok" | "error" | "declined";

export interface AgentEvent {
  id: number;
  tool_call_id: string;
  approval_id: string | null;
  tool: string;
  source: "andersen" | "quantfolio";
  tool_class: "read" | "draft";
  target: string | null;
  args: unknown;
  edited_args: unknown;
  status: EventStatus;
  result_summary: string | null;
  approved_by: string | null;
  actor: string;
  duration_ms: number | null;
  created_at: string;
}

export type Item =
  | { kind: "user"; text: string; at: string }
  | { kind: "assistant"; text: string; at: string; model: string | null; cost: number | null }
  | { kind: "tool"; event: AgentEvent };

export interface AgentConfig {
  provider: string;
  model: string;
  fallbackReason: string | null;
  maxCostPerThread: number;
  quantfolio: { transport: string; host: string };
}

export type StreamEvent =
  | { type: "text"; delta: string }
  | { type: "tool-call"; toolCallId: string; tool: string; source: string; cls: string; target: string | null; args: unknown }
  | { type: "tool-result"; toolCallId: string; ok: boolean; summary: string; durationMs: number }
  | {
      type: "approval";
      approvalId: string;
      toolCallId: string;
      tool: string;
      source: string;
      cls: string;
      target: string | null;
      args: unknown;
      description: string;
    }
  | { type: "denied"; toolCallId: string; reason: string | null }
  | { type: "usage"; tokensIn: number; tokensOut: number; cost: number; basis: string; threadCost: number; cap: number }
  | { type: "thread"; thread: AgentThread }
  | { type: "notice"; message: string }
  | { type: "error"; message: string }
  | { type: "done"; reason: "stop" | "awaiting-approval" | "cost-cap" | "error" };

async function http<T>(method: string, url: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { "X-Requested-With": "fetch" };
  const init: RequestInit = { method, credentials: "include", headers };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const res = await fetch(url, init);
  let data: Record<string, unknown> = {};
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    /* non-JSON body */
  }
  if (!res.ok) throw new Error(typeof data.error === "string" ? data.error : `HTTP ${res.status}`);
  return data as T;
}

/**
 * POST that answers with an SSE stream (or JSON when there is nothing to stream,
 * e.g. an approval while other cards are still open). Calls onEvent per event.
 */
async function postStream(
  url: string,
  body: unknown,
  onEvent: (e: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<{ streamed: boolean; json?: Record<string, unknown> }> {
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "X-Requested-With": "fetch", "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body),
    signal,
  });
  const type = res.headers.get("content-type") ?? "";
  if (!type.includes("text/event-stream")) {
    const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) throw new Error(typeof json.error === "string" ? json.error : `HTTP ${res.status}`);
    return { streamed: false, json };
  }
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      const block = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const data = block
        .split("\n")
        .filter((l) => l.startsWith("data:"))
        .map((l) => l.slice(5).trimStart())
        .join("\n");
      if (!data) continue; // keep-alive comment
      try {
        onEvent(JSON.parse(data) as StreamEvent);
      } catch {
        /* malformed event: skip */
      }
    }
  }
  return { streamed: true };
}

export const agentApi = {
  config: () => http<AgentConfig>("GET", "/api/agent/config"),
  threads: (advisorySessionId?: number) =>
    http<{ threads: AgentThread[] }>(
      "GET",
      `/api/agent/threads${advisorySessionId ? `?advisorySessionId=${advisorySessionId}` : ""}`,
    ),
  create: (input: { investorId?: number; advisorySessionId?: number; model?: "mock" }) =>
    http<{ thread: AgentThread; fallbackReason: string | null }>("POST", "/api/agent/threads", input),
  get: (id: number) =>
    http<{ thread: AgentThread; items: Item[]; pending: AgentEvent[]; busy: boolean; maxCostPerThread: number }>(
      "GET",
      `/api/agent/threads/${id}`,
    ),
  patch: (id: number, body: { title?: string; allowDrafts?: boolean; advisorySessionId?: number }) =>
    http<{ thread: AgentThread; backfilled: number }>("PATCH", `/api/agent/threads/${id}`, body),
  remove: (id: number) => http<{ ok: true }>("DELETE", `/api/agent/threads/${id}`),
  send: (id: number, text: string, onEvent: (e: StreamEvent) => void, signal?: AbortSignal) =>
    postStream(`/api/agent/threads/${id}/messages`, { text }, onEvent, signal),
  decide: (
    id: number,
    approvalId: string,
    body: { decision: "approve" | "decline"; args?: Record<string, unknown>; reason?: string; allowDrafts?: boolean },
    onEvent: (e: StreamEvent) => void,
  ) => postStream(`/api/agent/threads/${id}/approvals/${encodeURIComponent(approvalId)}`, body, onEvent),
  /** A5 hand-off: the M14 workbench adopts the QP instance the agent prepared. */
  adopt: (investorId: number, flowInstanceId: string) =>
    http<{ session: { id: number }; adopted: boolean }>("POST", "/api/advisory/sessions", { investorId, flowInstanceId }),
};
